/**
 * @fileoverview Edit User form hook.
 * @description The Edit dialog's form logic: the fields filled from one version of the user's
 * details, validated by the schema (placement is only validated when it can be changed), the
 * translated field errors, and the submit handler that sends only what changed.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  buildEditUserFormSchema,
  type EditUserFormValues,
} from '@/features/users/schemas/edit-user-form.schema'
import {
  canChangePlacement,
  toFormValues,
  toUpdateUserRequest,
} from '@/features/users/utils/edit-user.utils'

import type { UpdateUserRequest, UserDetail } from '@/features/users/types/users.types'
import type { FormEvent } from 'react'

/**
 * @description Runs the Edit form: its fields, validation and submit.
 * @param {UserDetail} user - The user, as the form is filled from.
 * @param {(payload: UpdateUserRequest) => void} submitChanges - What to do with the changed fields
 * once the form is valid.
 * @returns {object} The form, whether placement is editable, the translated field errors, and the
 * submit handler for the form element.
 */
export function useEditUserForm(
  user: UserDetail,
  submitChanges: (payload: UpdateUserRequest) => void,
) {
  const { t } = useTranslation()
  const placementEditable = canChangePlacement(user)
  const form = useForm<EditUserFormValues>({
    resolver: zodResolver(
      buildEditUserFormSchema({
        currentMobileNumber: user.mobileNumber ?? '',
        canChangePlacement: placementEditable,
      }),
    ),
    defaultValues: toFormValues(user),
  })
  const { errors } = form.formState

  /**
   * @description Translates a validation message key, if there is one.
   * @param {string | undefined} key - The i18n key from the schema, if the field is invalid.
   * @returns {string | undefined} The message to show.
   */
  const message = (key: string | undefined) => (key ? t(key) : undefined)

  /**
   * @description Sends the changed fields.
   * @param {EditUserFormValues} values - The validated values.
   */
  const onValid = (values: EditUserFormValues) => {
    submitChanges(toUpdateUserRequest(values, user))
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void form.handleSubmit(onValid)(event)
  }

  return {
    form,
    placementEditable,
    firstNameError: message(errors.firstName?.message),
    lastNameError: message(errors.lastName?.message),
    middleNameError: message(errors.middleName?.message),
    mobileNumberError: message(errors.mobileNumber?.message),
    onSubmit,
  }
}
